import { User } from '@nextforce/api'
import { Icon, Text, makeStyles, useTheme } from '@rneui/themed'
import { t } from 'app/i18l'
import { ColorValue, StyleProp, View, ViewStyle } from 'react-native'
import { SafeAreaView } from 'react-native-safe-area-context'
import { Box, DEFAULT_BOX_HEIGHT } from 'shared/components/rectangle'

const sampleUser: User = {
  id: 'sample',
  crewPerformanceStats: {
    appOpen: 0.6,
    nextShiftInterests: 0.3,
    nextShiftsWorked: 3,
  },
  userRatingLifetime: { rating: 4.3, ratingsCount: 3 },
}

export function AnytimeCrewMetricsView() {
  const styles = useStyles()
  return (
    <SafeAreaView edges={['left', 'right', 'bottom']} style={styles.root}>
      <View style={styles.container}>
        <View style={styles.innerContainer}>
          <View style={styles.section}>
            <Text style={styles.text}>
              {t('metricsCaluculatedOverLastFourWeeks')}
            </Text>
            <CrewMetrics
              user={sampleUser}
              statStyle={styles.statStyle}
              showLabels
            />
          </View>
          <View style={styles.section}>
            <Text style={styles.text}>A. NEXT App</Text>
            <View style={styles.rectangleRow}>
              <Box color={getMetricColorCode(0.1)} />
              <Text style={styles.rectangleText}>{t('notActiveUser')}</Text>
            </View>
            <View style={styles.rectangleRow}>
              <Box color={getMetricColorCode(0.3)} />
              <Text style={styles.rectangleText}>{t('occasionalUser')}</Text>
            </View>
            <View style={styles.rectangleRow}>
              <Box color={getMetricColorCode(0.6)} />
              <Text style={styles.rectangleText}>{t('regularUser')}</Text>
            </View>
          </View>
          <View style={styles.section}>
            <Text style={styles.text}>B. NEXT Shift Interest</Text>
            <View style={styles.rectangleRow}>
              <Box color={getMetricColorCode(0.1)} />
              <Text style={styles.rectangleText}>{t('notActive')}</Text>
            </View>
            <View style={styles.rectangleRow}>
              <Box color={getMetricColorCode(0.3)} />
              <Text style={styles.rectangleText}>
                {t('occasionalInterest')}
              </Text>
            </View>
            <View style={styles.rectangleRow}>
              <Box color={getMetricColorCode(0.6)} />
              <Text style={styles.rectangleText}>{t('regularInterest')}</Text>
            </View>
          </View>
          <View style={styles.section}>
            <Text style={styles.text}>
              {`C. ${t('NEXTShiftsWorkedOverLastFourWeeks')}`}
            </Text>
          </View>
          <View style={styles.section}>
            <Text style={styles.text}>{`D. ${t('NEXTShiftRating')}`}</Text>
          </View>
        </View>
      </View>
    </SafeAreaView>
  )
}

export function CrewMetrics({
  user,
  containerStyle,
  statStyle,
  showLabels,
}: {
  user: User
  showLabels?: boolean
  containerStyle?: StyleProp<ViewStyle>
  statStyle?: StyleProp<ViewStyle>
}) {
  const styles = useStyles()
  const { theme } = useTheme()
  return (
    <View
      style={[
        {
          flexDirection: 'row',
          alignItems: 'center',
        },
        containerStyle,
      ]}
    >
      {user.crewPerformanceStats?.appOpen !== undefined ? (
        <View style={[styles.statContainer, statStyle]}>
          <Box
            color={getMetricColorCode(user.crewPerformanceStats?.appOpen)}
            style={{ marginHorizontal: 5 }}
          />
          {showLabels && <Text style={styles.labelTextStyle}>A</Text>}
        </View>
      ) : null}
      {user.crewPerformanceStats?.nextShiftInterests !== undefined ? (
        <View style={[{ alignItems: 'center' }, statStyle]}>
          <Box
            color={getMetricColorCode(
              user.crewPerformanceStats?.nextShiftInterests
            )}
            style={{ marginHorizontal: 5 }}
          />
          {showLabels && <Text style={styles.labelTextStyle}>B</Text>}
        </View>
      ) : null}
      {user.crewPerformanceStats?.nextShiftsWorked !== undefined ? (
        <View style={[{ alignItems: 'center' }, statStyle]}>
          <Text
            style={[
              styles.stat,
              { height: DEFAULT_BOX_HEIGHT, lineHeight: DEFAULT_BOX_HEIGHT },
            ]}
          >
            {user.crewPerformanceStats?.nextShiftsWorked}
          </Text>
          {showLabels && <Text style={[styles.labelTextStyle]}>C</Text>}
        </View>
      ) : null}
      {user.userRatingLifetime?.rating !== undefined ? (
        user.userRatingLifetime?.ratingsCount !== undefined ? (
          <View style={[{ alignItems: 'center' }, statStyle]}>
            <View style={{ flexDirection: 'row' }}>
              <Icon
                name={'star'}
                type="material-design"
                color={theme.colors.primary}
                size={DEFAULT_BOX_HEIGHT}
                style={{ height: DEFAULT_BOX_HEIGHT }}
              />
              <Text style={styles.stat}>
                {`${user.userRatingLifetime?.rating} (${user.userRatingLifetime?.ratingsCount})`}
              </Text>
            </View>
            {showLabels && <Text style={styles.labelTextStyle}>D</Text>}
          </View>
        ) : null
      ) : null}
    </View>
  )
}

export function getMetricColorCode(metric: number): ColorValue {
  if (metric < 0.25) {
    return '#353B4A'
  } else if (metric < 0.5) {
    return '#ACCFD5'
  } else {
    return '#89ACAF'
  }
}

const useStyles = makeStyles(theme => ({
  root: {
    flex: 1,
  },
  container: {
    flex: 1,
    paddingTop: 10,
    backgroundColor: theme.colors.grey5,
  },
  innerContainer: {
    flex: 1,
    backgroundColor: theme.colors.white,
    padding: 20,
  },
  text: { color: theme.colors.grey2, paddingBottom: 20 },
  section: { paddingBottom: 30 },

  rectangleRow: { flexDirection: 'row', marginBottom: 5, alignItems: 'center' },
  rectangleText: { marginLeft: 10, color: theme.colors.grey2 },
  statStyle: { margin: 10, transform: [{ scale: 1.2 }] },
  stat: {
    marginHorizontal: 6,
    color: theme.colors.grey2,
    fontWeight: 'bold',
    textAlignVertical: 'center',
    textAlign: 'center',
  },
  labelTextStyle: {
    marginTop: 15,
    color: theme.colors.grey3,
    fontSize: 16,
    alignSelf: 'center',
  },
  statContainer: { alignItems: 'center' },
}))
